import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { observer } from "mobx-react-lite";
import { Button, Col, Form, Row } from "react-bootstrap";

import { useAppStore } from "../../../providers/AppStoreProvider";
import { useAPI } from "../../../providers/ApiProvider";
import { toastWithAsyncFetch } from "../../../utils/helper";
import Tiptap from "../../TipTap/TipTap";

export const BaseForm = observer(
  forwardRef(({ currentLesson, onHideModal, dangerConfirming, setDangerConfirming }, ref) => {
    const store = useAppStore();
    const api = useAPI();
    const nameRef = useRef(null);
    const chapterRef = useRef(null);
    const descriptionRef = useRef(null);

    useImperativeHandle(ref, () => ({
      getBaseData() {
        return {
          name: nameRef.current.value,
          chapter: parseInt(chapterRef.current.value),
          description: descriptionRef.current.getHtml(),
        };
      },
    }));

    useEffect(() => {
      if (store.lessonModal.mode === "edit") {
        nameRef.current.value = currentLesson.name;
        chapterRef.current.value = currentLesson.chapter.id;
        setTimeout(() => {
          descriptionRef.current?.setContent(currentLesson.description);
        }, 0);
      }
    }, [store.lessonModal.entityId]);

    const deleteLessonHandler = () => {
      toastWithAsyncFetch(
        {
          loading: "Đang xoá bài học...",
          success: "Xoá bài học thành công",
          error: "Xoá bài học thất bại!",
        },
        api.callApi.bind(null, "deleteLesson", { params: { pk: currentLesson.id } }),
        () => {
          onHideModal();
          store.deleteLesson(currentLesson.id);
        },
      );
    };

    return (
      <>
        <Form.Group>
          <Form.Label className="fw-bold">Tên bài học</Form.Label>
          <Form.Control
            name="name"
            ref={nameRef}
            placeholder="Ví dụ: Giới thiệu về thanh công cụ trong Photoshop"
            className="rounded-10 p-3"
          />
          <div className="fs-7 text-black-50 mt-2">Gợi ý: Giữ tên bài học ngắn gọn</div>
        </Form.Group>

        <Form.Group as={Row} className="flex-grow-1 mt-4">
          <Col lg={{ span: 7 }}>
            <Form.Label className="fw-bold">Chương học</Form.Label>
            <Form.Select
              name="chapter"
              ref={chapterRef}
              placeholder="Chọn dạng bài học"
              className="rounded-10 p-3"
            >
              {store.chapters.map((chapter) => (
                <option value={chapter.id} key={`chapter-${chapter.id}`}>
                  {chapter.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col lg={{ span: 5 }}>
            <Form.Label className="fw-bold">Số thứ tự trong chương</Form.Label>
            <Form.Control
              name="order"
              placeholder="Ví dụ: Giới thiệu về thanh công cụ trong Photoshop"
              className="rounded-10 p-3"
              value="--"
              disabled
            />
          </Col>
        </Form.Group>

        <Form.Group className="mt-4">
          <Form.Label className="fw-bold">Mô tả chi tiết</Form.Label>
          <Tiptap ref={descriptionRef} placeholder="Mô tả ngắn của bài học" />
          <div className="fs-7 text-black-50 mt-2">
            Gợi ý: Mô tả ngắn của bài học nên tóm lược được tổng quan của bài học đó
          </div>
        </Form.Group>

        {store.lessonModal.mode === "edit" && store.hasPermission("delete_lesson") && (
          <Form.Group className="mt-5">
            <Form.Label className="fw-bold mb-1">Danger Zone</Form.Label>
            <div className="fs-7 text-black-50 mb-3">Thao tác này không thể hoàn tác</div>
            <div>
              {dangerConfirming ? (
                <div className="danger-confirm rounded-10">
                  <div className="fw-bold ms-1">Warning</div>
                  <div className="fs-7 text-black-50 ms-1 mb-3">
                    Bạn có chắc chắn xoá bài học này?
                  </div>
                  <div className="d-flex flex-row">
                    <Button
                      variant="danger"
                      className="rounded-pill me-2 px-4"
                      onClick={deleteLessonHandler}
                    >
                      Xoá bài học
                    </Button>
                    <Button
                      variant="outline"
                      className="outline-0 text-black-50 rounded-pill border-0 p-2 px-4"
                      onClick={() => setDangerConfirming(false)}
                    >
                      Hủy
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  role="button"
                  className="delete-btn rounded-10 py-2 px-3"
                  onClick={() => setDangerConfirming(true)}
                >
                  Xoá bài học này
                </div>
              )}
            </div>
          </Form.Group>
        )}
      </>
    );
  }),
);
